import React from 'react';
import { Box, Typography } from '@mui/material';
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import OpenTicketCard from './OpenTicketCard';
import './AttendantQueue.css';

const formatDuration = (value) => {
  if (!value || typeof value !== 'string') return '--';
  const parts = value.split(':');
  if (parts.length !== 3) return value;
  const [hoursRaw, minutesRaw, secondsRaw] = parts;
  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = hoursRaw.includes('.') ? hoursRaw : hoursRaw.padStart(2, '0');
  const minutes = minutesRaw.padStart(2, '0');
  const seconds = secondsPart.padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

const parseDurationToSeconds = (value) => {
  if (!value || typeof value !== 'string') return null;
  const parts = value.split(':');
  if (parts.length !== 3) return null;
  const [hoursRaw, minutesRaw, secondsRaw] = parts;
  const secondsPart = secondsRaw.split(/[.,]/)[0];
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsPart);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return null;
  }
  return hours * 3600 + minutes * 60 + seconds;
};

const getStatus = (avgSeconds, slaSeconds) => {
  if (!Number.isFinite(avgSeconds) || !Number.isFinite(slaSeconds) || slaSeconds <= 0) {
    return null;
  }
  const ratio = avgSeconds / slaSeconds;
  if (ratio <= 0.75) return 'ok';
  if (ratio < 1) return 'warn';
  return 'alert';
};

const getPercent = (avgSeconds, slaSeconds) => {
  if (!Number.isFinite(avgSeconds) || !Number.isFinite(slaSeconds) || slaSeconds <= 0) {
    return null;
  }
  return Math.round((avgSeconds / slaSeconds) * 100);
};

const formatDateTime = (value) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
};

const normalizePresenceStatus = (attendantMetrics) => {
  const rawStatus = [
    attendantMetrics?.currentStatus,
    attendantMetrics?.status,
    attendantMetrics?.presence,
    attendantMetrics?.availability,
    attendantMetrics?.userData?.status,
    attendantMetrics?.userData?.presence,
    attendantMetrics?.userData?.availability,
  ].find((value) => value !== undefined && value !== null && `${value}`.trim() !== '');

  if (typeof rawStatus === 'boolean') {
    return rawStatus ? 'online' : 'offline';
  }

  const normalized = String(rawStatus || '').trim().toLowerCase();
  if (!normalized) return 'unknown';

  if (['online', 'available', 'active', 'ready', 'on'].includes(normalized)) {
    return 'online';
  }

  if (['away', 'busy', 'idle', 'break', 'pause', 'paused'].includes(normalized)) {
    return 'away';
  }

  if (['offline', 'unavailable', 'disconnected', 'off'].includes(normalized)) {
    return 'offline';
  }

  return 'unknown';
};

const getPresenceLabel = (presenceStatus) => {
  if (presenceStatus === 'online') return 'Online';
  if (presenceStatus === 'away') return 'Ausente';
  if (presenceStatus === 'offline') return 'Offline';
  return 'Status indisponível';
};

const AttendantQueue = ({ attendantName, tickets, attendantMetrics, slaMetrics, stackMetrics = false }) => {
  const openedTickets = attendantMetrics?.openedTickets ?? tickets?.length ?? 0;
  const closedTickets = attendantMetrics?.closedTickets ?? '--';
  const averageAttendanceRaw = attendantMetrics?.averageAttendanceTime;
  const averageResponseRaw = attendantMetrics?.averageResponseTime;
  const averageAttendanceTime = formatDuration(averageAttendanceRaw);
  const averageResponseTime = formatDuration(averageResponseRaw);
  const updatedAt = formatDateTime(attendantMetrics?.currentStatusDateTime);
  const attendanceSeconds = parseDurationToSeconds(averageAttendanceRaw);
  const responseSeconds = parseDurationToSeconds(averageResponseRaw);
  const attendanceSlaSeconds = Number(slaMetrics?.attendance_time);
  const responseSlaSeconds = Number(slaMetrics?.response_time);
  const attendanceStatus = getStatus(attendanceSeconds, attendanceSlaSeconds);
  const responseStatus = getStatus(responseSeconds, responseSlaSeconds);
  const attendancePercent = getPercent(attendanceSeconds, attendanceSlaSeconds);
  const responsePercent = getPercent(responseSeconds, responseSlaSeconds);
  const attendancePercentLabel = Number.isFinite(attendancePercent) ? `${attendancePercent}%` : '--';
  const responsePercentLabel = Number.isFinite(responsePercent) ? `${responsePercent}%` : '--';
  const presenceStatus = normalizePresenceStatus(attendantMetrics);
  const presenceLabel = getPresenceLabel(presenceStatus);

  return (
    <Box className="attendantQueue">
      <Box className="attendantQueue__header">
        <Box className="attendantQueue__titleRow">
          <Typography className="attendantQueue__title" title={attendantName}>
            {attendantName}
          </Typography>
          <Box className={`attendantQueue__presence attendantQueue__presence--${presenceStatus}`} title={`Status: ${presenceLabel}`}>
            <span className="attendantQueue__presenceDot" />
            <span className="attendantQueue__presenceText">{presenceLabel}</span>
          </Box>
        </Box>
        <Box className="attendantQueue__line attendantQueue__line--tickets">
          <Box className="attendantQueue__ticketMetric">
            <MarkChatUnreadOutlinedIcon className="attendantQueue__ticketIcon" />
            <span className="attendantQueue__ticketValue">{openedTickets}</span>
          </Box>
          <Box className="attendantQueue__ticketMetric">
            <DoneAllOutlinedIcon className="attendantQueue__ticketIcon" />
            <span className="attendantQueue__ticketValue">{closedTickets}</span>
          </Box>
        </Box>
        {stackMetrics ? (
          <>
            <Box className="attendantQueue__metricStack">
              <span className="attendantQueue__label">Atendimento</span>
              <span className={`attendantQueue__value${attendanceStatus ? ` attendantQueue__value--${attendanceStatus}` : ''}`}>
                {averageAttendanceTime}
                <span className="attendantQueue__percent">{attendancePercentLabel}</span>
              </span>
            </Box>
            <Box className="attendantQueue__metricStack">
              <span className="attendantQueue__label">Resposta</span>
              <span className={`attendantQueue__value${responseStatus ? ` attendantQueue__value--${responseStatus}` : ''}`}>
                {averageResponseTime}
                <span className="attendantQueue__percent">{responsePercentLabel}</span>
              </span>
            </Box>
          </>
        ) : (
          <>
            <Box className="attendantQueue__line attendantQueue__line--labels">
              <span className="attendantQueue__label">Atendimento</span>
              <span className="attendantQueue__label">Resposta</span>
            </Box>
            <Box className="attendantQueue__line attendantQueue__line--values">
              <span className={`attendantQueue__value${attendanceStatus ? ` attendantQueue__value--${attendanceStatus}` : ''}`}>
                {averageAttendanceTime}
                <span className="attendantQueue__percent">{attendancePercentLabel}</span>
              </span>
              <span className={`attendantQueue__value${responseStatus ? ` attendantQueue__value--${responseStatus}` : ''}`}>
                {averageResponseTime}
                <span className="attendantQueue__percent">{responsePercentLabel}</span>
              </span>
            </Box>
          </>
        )}
        <Typography
          className={`attendantQueue__updated${stackMetrics ? ' attendantQueue__updated--compact' : ''}`}
          color="text.secondary"
        >
          Atualizado em: {updatedAt}
        </Typography>
      </Box>
      <Box className="attendantQueue__body">
        {tickets.map((ticket, index) => (
          <OpenTicketCard
            key={`${ticket?.sequentialId || 'ticket'}-${index}`}
            ticket={ticket}
            slaMetrics={slaMetrics}
            stackMetrics={stackMetrics}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AttendantQueue;
